import { Box, Card, Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { ReactElement, memo } from 'react'
import { Edit, Delete, Image } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import type { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd'
import { getS3ImageFilePath } from 'src/v3/utilities/image-upload'
import { DragAndDropIcon } from 'src/v3/core/icons'
import { SeriesResponse } from '../types'
import { CREATE_SERIES_URLS } from '../../constants'

type SeriesLineItemProps = {
  series: SeriesResponse
  linkedServiceName: string | undefined
  onDelete: (series: SeriesResponse) => void
  dragHandleProps?: DraggableProvidedDragHandleProps
}

let SeriesLineItem = memo(
  ({
    series,
    linkedServiceName,
    onDelete,
    dragHandleProps,
  }: SeriesLineItemProps): ReactElement => {
    let navigate = useNavigate()
    let seasonCount = new Set(series.courses.map((c) => c.season)).size
    let episodeCount = series.courses.length

    return (
      <Card
        className="flex flex-row items-center transition-shadow duration-200"
        sx={{ bgcolor: 'background.paperDark' }}
      >
        {dragHandleProps && (
          <Box
            {...dragHandleProps}
            className="flex items-center px-2"
            sx={{ color: 'text.disabled', cursor: 'grab' }}
            aria-label="Drag to reorder"
          >
            <DragAndDropIcon aria-hidden="true" />
          </Box>
        )}
        <Box
          className="flex items-center justify-center shrink-0 w-16 h-16 border-r"
          sx={{ bgcolor: 'background.paperDark', borderColor: 'divider' }}
        >
          {series.thumbnail ? (
            <img
              src={getS3ImageFilePath(series.thumbnail)}
              alt={`Thumbnail for ${series.name}`}
              className="object-cover w-full h-full"
            />
          ) : (
            <Image sx={{ color: 'text.disabled' }} aria-hidden="true" />
          )}
        </Box>
        <Box className="flex flex-1 items-center gap-3 px-3 py-2 min-w-0">
          <Box className="flex-1 min-w-0">
            <Box className="flex items-center gap-2">
              <Typography
                variant="body1"
                component="h3"
                className="truncate font-semibold"
              >
                {series.name}
              </Typography>
              <Chip
                label={series.published === 'published' ? 'Published' : 'Draft'}
                color={series.published === 'published' ? 'success' : 'warning'}
                size="small"
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {linkedServiceName && `${linkedServiceName} · `}
              {seasonCount} season{seasonCount !== 1 ? 's' : ''} ·{' '}
              {episodeCount} episode{episodeCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <Box className="flex gap-1 shrink-0">
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => navigate(CREATE_SERIES_URLS.EDIT(series.id))}
                aria-label={`Edit ${series.name}`}
              >
                <Edit fontSize="medium" color="primary" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                series.published === 'published'
                  ? 'Unpublish the series before deleting'
                  : 'Delete'
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(series)}
                  disabled={series.published === 'published'}
                  aria-label={`Delete ${series.name}`}
                >
                  <Delete fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Card>
    )
  },
  (prevProps, nextProps) =>
    prevProps.series.id === nextProps.series.id &&
    prevProps.series.name === nextProps.series.name &&
    prevProps.series.published === nextProps.series.published &&
    prevProps.series.thumbnail === nextProps.series.thumbnail &&
    prevProps.series.courses === nextProps.series.courses &&
    prevProps.linkedServiceName === nextProps.linkedServiceName &&
    (prevProps.dragHandleProps !== undefined) ===
      (nextProps.dragHandleProps !== undefined),
)

export default SeriesLineItem
